import React from 'react';
import {
	FormattedMessage,
	FormattedNumber,
	defineMessages,
} from 'react-intl';

import ExportHeaderButton from '~/components/app/ExportHeaderButton';
import HeaderButtonsLayout from '~/components/atoms/screenLayouts/components/header/layouts/HeaderButtonsLayout';
import HeaderLayout from '~/components/logic/pagesOverview/HeaderLayout';
import HeaderOptionsSwitchLayout from '~/components/atoms/screenLayouts/components/header/layouts/HeaderOptionsSwitchLayout';
import HeaderTitle, {
	SIZE_SMALL as TITLE_SIZE_SMALL,
} from '~/components/atoms/screenLayouts/components/header/headerTitle/HeaderTitle';
import HeaderTitleLayout from '~/components/atoms/screenLayouts/components/header/layouts/HeaderTitleLayout';
import HeaderTitleNumericValue from '~/components/atoms/screenLayouts/components/header/headerTitle/builders/HeaderTitleNumericValue';
import HeaderTitleValue, {
	STYLE_DECENT as TITLE_VALUE_STYLE_DECENT,
} from '~/components/atoms/screenLayouts/components/header/headerTitle/builders/HeaderTitleValue';
import ManageColumnsLink from '~/components/logic/columnsConfigurator/ManageColumnsLink';
import PagesOverviewFilterBar from '~/components/logic/pagesOverview/activeTableFilterBar/PagesOverviewFilterBar';
import PagesViewSwitch from '~/components/logic/pagesOverview/PagesViewSwitch';
import TrackedChangesCalendar from '~/components/logic/pagesOverview/TrackedChangesCalendar';

import {
	usePagesOverviewHeaderQuery,
} from './PagesOverviewHeader.gql';

import useDatatableContext from '~/hooks/useDatatableContext';
import useExportPages from '~/hooks/useExportPages';
import useFilteredPages from '~/hooks/useFilteredPages';
import usePollInterval from '~/hooks/usePollInterval';
import useWebsiteId from '~/hooks/useWebsiteId';

import {
	calculatePagesOverviewPercentage,
} from '~/model/pages';

import {
	PAGES_SECTION_TABLE,
} from '~/model/ui/pagesScreen';

// import {
// 	totalSelector as totalFilteredGraphPagesSelector,
// } from '~/state/pagesGraphs/selectors';



const messages = defineMessages({
	export: {
		id: 'ui.general.export',
	},
	title: {
		id: 'ui.contentOverview.title',
	},
});



function calculatePagesNumber({
	activeFilter,
	pageCapacity,
	filteredPageCount,
}: {
	activeFilter: any,
	pageCapacity: number,
	filteredPageCount: number | null,
}) {
	if (filteredPageCount === null) {
		return null;
	}

	return (
		filteredPageCount > pageCapacity
		&& activeFilter.has('type')
		&& activeFilter.get('type').size === 1
		&& activeFilter.get('type').first() === 'page'
	)
		? pageCapacity
		: filteredPageCount;
}



const PagesOverviewHeader = (props) => {
	const {
		activeFilter,
		pagesSection,
	} = props;

	const filteredPages = useFilteredPages();
	const websiteId = useWebsiteId();

	const {
		scrollToColumn,
	} = useDatatableContext();

	const {
		data,
	} = usePagesOverviewHeaderQuery({
		pollInterval: usePollInterval(30000),
		variables: {
			websiteId,
		},
	});

	const websiteData = (data && data.website) || null;

	const titleSuffix: Array<React.ReactNode> = [];

	let totalFilteredPages: number | null = null;

	if (websiteData !== null) {
		totalFilteredPages = calculatePagesNumber({
			activeFilter,
			pageCapacity: websiteData.pageCapacity,
			filteredPageCount: filteredPages.total,
		});

		const percentage = calculatePagesOverviewPercentage({
			matchedPages: totalFilteredPages,
			totalPages: websiteData.pageTotal,
		});

		if ((websiteData.pageTotal ?? 0) > 0 && totalFilteredPages !== null) {
			titleSuffix.push(
				<HeaderTitleNumericValue
					key="title-numeric-value"
					value={totalFilteredPages}
				/>,
			);
		}

		if (percentage !== null) {
			titleSuffix.push(
				<HeaderTitleValue
					key="title-percentage"
					style={TITLE_VALUE_STYLE_DECENT}
					value={(
						<FormattedNumber
							maximumFractionDigits={2}
							minimumFractionDigits={0}
							style="percent"
							value={percentage}
						/>
					)}
				/>,
			);
		}
	}

	const exportPages = useExportPages(totalFilteredPages);

	return (
		<HeaderLayout
			filter={(
				<PagesOverviewFilterBar
					onFilterClickCallback={(filterName) => {
						scrollToColumn?.(filterName);
					}}
				/>
			)}
			switchElement={websiteData !== null && (
				<HeaderOptionsSwitchLayout>
					<PagesViewSwitch />
				</HeaderOptionsSwitchLayout>
			)}
			title={(
				<HeaderTitleLayout keepPredefinedWidth={true}>
					<HeaderTitle
						size={TITLE_SIZE_SMALL}
						value={titleSuffix}
					>
						<FormattedMessage {...messages.title} />
					</HeaderTitle>
					<ManageColumnsLink />
				</HeaderTitleLayout>
			)}
		>
			<HeaderButtonsLayout>
				{pagesSection === PAGES_SECTION_TABLE && (
					<TrackedChangesCalendar />
				)}

				{pagesSection === PAGES_SECTION_TABLE && (
					<ExportHeaderButton
						createExport={exportPages}
						linkLabel={(
							<FormattedMessage {...messages.export} />
						)}
					/>
				)}
			</HeaderButtonsLayout>
		</HeaderLayout>
	);
};



export default PagesOverviewHeader;
